import React, { useCallback, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'

import { ReactComponent as ArrowDownSVG } from 'shared/img/arrow-down.svg'
import { ReactComponent as UserSVG } from 'shared/img/user-svg.svg'

import { useOutsideClick } from 'shared/hooks/useOutsideClick'

import { logout } from 'features/auth/actions'
import { selectAuthCommunication, selectIsUserAuthenticated, selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import { profilePageItems } from 'components/Header/data'
import Button from 'components/UIKit/Button/Button'

import './UserBlock.scss'

const b = block('user-block')

const UserBlock: React.FC = () => {
  const modal = useModalContext()

  const dispatch = useDispatch()

  const locale = useSelector(selectLocaleDict, shallowEqual).profile
  const user = useSelector(selectUserData, shallowEqual)
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  const profileMenuRef = useRef(null)

  const [profileMenuOpen, setProfileMenuOpen] = useState(false)

  useOutsideClick(profileMenuRef, () => setProfileMenuOpen(false))

  const listItems = useMemo(
    () =>
      profilePageItems.accountSettings.items
        .concat(profilePageItems.playerActivities.items)
        .filter(item => !item.notDesktop)
        .map(item => (
          <Link key={item.desktopRoute} to={item.desktopRoute} className={b('item')}>
            <span className={b('link-text')}>{locale[item.label]}</span>
          </Link>
        )),
    [locale],
  )

  const onUserBlockClick = () => setProfileMenuOpen(prev => !prev)

  const onLogoutClick = useCallback(() => {
    dispatch(logout())
  }, [dispatch])

  return (
    <section className={b()}>
      {isAuth ? (
        <div className={b('user')} onClick={onUserBlockClick}>
          <div className={b('icon-wrapper')}>
            <UserSVG className={b('icon')} />
          </div>
          <div className={b('info')}>
            <span className={b('info-username')}>{user?.username}</span>
            <span className={b('info-amount')}>{`${user?.balance} ${user?.currency}`}</span>
          </div>

          {profileMenuOpen && (
            <div className={b('items')} ref={profileMenuRef}>
              {listItems}
              <div className={b('item')}>
                <span className={b('link-text')} onClick={onLogoutClick}>
                  {locale.logout}
                </span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={b('buttons-wrapper')}>
          <div className={b('btn-container')}>
            <Button text={locale.signUp} callback={modal.pushNewModal(MODAL_ROUTE_URLS.SIGN_UP())} />
          </div>
          <div className={b('btn-container')}>
            <Button text={locale.login} callback={modal.pushNewModal(MODAL_ROUTE_URLS.SIGN_IN())} />
          </div>
        </div>
      )}
    </section>
  )
}

export default UserBlock
